/**
 * THE META PIXEL'S COOKIES, AS HEADERS FOR THE ORDER REQUEST.
 *
 * The server sends Meta its own Purchase for a paid storefront order (the Conversions API), and
 * Meta matches that event to the shopper's browser by the pixel's first-party cookies: `_fbp`
 * (the browser) and `_fbc` (the ad click). The storefront is another site than the API, so the
 * cookies themselves never reach the server. The order request copies them into `X-Meta-Fbp` /
 * `X-Meta-Fbc`, which the public order route reads (stylon-server ads/meta/shop-purchase-event.ts).
 *
 * Only a value in the cookie's own format is copied — the same rule the server holds them to, so
 * nothing is sent that it would drop — and that also keeps anything a header cannot carry (a
 * character outside Latin-1, a line break) out of the request: the browser would throw on it
 * before the order left.
 */

/** `fb.<subdomain index>.<creation time>.<id>`, with Meta's optional appendix. */
const PIXEL_COOKIE = /^fb\.\d\.\d{10,16}\.[A-Za-z0-9_.-]{1,400}$/;

const COOKIE_HEADERS = [
  ['_fbp', 'X-Meta-Fbp'],
  ['_fbc', 'X-Meta-Fbc'],
];

/** The document's cookies, or none: a sandboxed document can refuse to show them. */
function documentCookies() {
  try {
    return typeof document !== 'undefined' ? String(document.cookie || '') : '';
  } catch (_) {
    return '';
  }
}

/** The first well-formed value of cookie `name`: the same name can be set on two domains. */
function pixelCookie(cookies, name) {
  for (const part of cookies.split(';')) {
    const eq = part.indexOf('=');
    if (eq < 0 || part.slice(0, eq).trim() !== name) continue;
    const value = part.slice(eq + 1).trim();
    if (PIXEL_COOKIE.test(value)) return value;
  }
  return null;
}

/** `{ 'X-Meta-Fbp': …, 'X-Meta-Fbc': … }` for the cookies this browser holds — empty when none. */
export function metaPixelCookieHeaders(cookies = documentCookies()) {
  const headers = {};
  COOKIE_HEADERS.forEach(([cookie, header]) => {
    const value = pixelCookie(String(cookies || ''), cookie);
    if (value) headers[header] = value;
  });
  return headers;
}
